import { myWorkspacesOptions } from '@doneverse/data/queries'
import type { MeWorkspace } from '@doneverse/shared'
import type { QueryClient } from '@tanstack/react-query'
import { useDashboardStore } from '@/store'

/**
 * Drop everything scoped to the active workspace (requests carry it via the
 * x-doneverse-workspace-id header). Only the user's own /v1/me data, the
 * workspace list itself, and the doer founder cards are user-scoped, so those
 * stay cached across a workspace change.
 *
 * `resetQueries`, NOT `invalidateQueries`. Invalidation marks a query stale and
 * refetches it, but React Query keeps serving the previous data with
 * `isLoading: false` until the new response lands — so for the seconds after a
 * doer switches founder, every workspace-scoped surface renders the PREVIOUS
 * founder's data as though it were the selected one's. On the launch checklist
 * that is one founder's private notes under another's name, and printable;
 * on /files and /tasks it is someone else's work.
 *
 * Resetting puts observed queries back to pending and refetches, so the switch
 * shows a loading state and then the right founder. The cost is a visible
 * skeleton where there used to be instant (wrong) content, and infinite queries
 * losing their loaded pages — both correct for "you are now looking at someone
 * else".
 */
export function resetWorkspaceScopedQueries(queryClient: QueryClient) {
  return queryClient.resetQueries({
    predicate: (q) => {
      const [first, second] = q.queryKey as unknown[]
      if (first === 'profile' && second === 'me') return false
      if (first === 'profile' && second === 'workspaces') return false
      // Founder cards are user-scoped (every founder, not the active one).
      if (first === 'profile' && second === 'founders') return false
      // A doer has ONE profile however many founders they work with. This is
      // `resetQueries`, not invalidate, so leaving it in would blank the editor
      // to its skeleton mid-edit when a multi-founder doer switches workspace.
      // `['doerProfiles','workspace']` is the founder's stacked view and DOES
      // belong to the active workspace, so it is deliberately not exempt.
      if (first === 'doerProfiles' && second === 'me') return false
      return true
    },
  })
}

/**
 * Establish the active workspace from the membership list that rides on
 * GET /v1/me. Called inside the profile queryFn — i.e. before
 * AuthBootstrapBoundary lets the router mount — so the very first
 * workspace-scoped prefetch already carries a valid workspace header instead
 * of racing the workspace list fetch and falling back to the API's legacy
 * header-less resolution.
 *
 * Keeps the persisted selection when it's still a membership; otherwise falls
 * back to the first workspace. Also seeds the workspace-list query cache so
 * the switcher doesn't refetch on boot.
 */
export function bootstrapWorkspaceSelection(
  queryClient: QueryClient,
  userId: string,
  workspaces: MeWorkspace[],
) {
  const store = useDashboardStore.getState()
  store.setCurrentUserId(userId)
  queryClient.setQueryData(myWorkspacesOptions(userId).queryKey, workspaces)
  if (workspaces.length === 0) return
  const stored = store.workspaceIdsByUserId[userId] ?? null
  if (stored && workspaces.some((w) => w.id === stored)) return
  store.setWorkspaceId(userId, workspaces[0].id)
  // A previously valid selection was replaced (membership revoked between
  // sessions or on a profile refetch): anything cached under the old header
  // is for the wrong workspace. On first boot the cache is empty, so this
  // only costs anything when it matters.
  if (stored) void resetWorkspaceScopedQueries(queryClient)
}
